import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { CheckIcon, CloseIcon, EditIcon, Input, SelectField } from "components";
import {
  useGetCustomerUserCategories,
  useUpdateCustomerUserCategory,
} from "controller";
import type { ChangeEvent, KeyboardEvent } from "react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { CustomerUserCategory, CustomerUserCategoryTier } from "shared";
import { getApiErrorMessage } from "utilz/helpers";
import {
  CrownTierIcon,
  useCustomerUserCategoryOptions,
} from "shared/features/customer-user-category";

// `useCustomerUserCategoryOptions` and the tier-derivation that feeds it were
// promoted to `shared/features/customer-user-category` (2026-09-02) so the
// ticket table can render the same crown ladder. Re-exported here because four
// files in this module already import it from this path.
export { useCustomerUserCategoryOptions };

export const CATEGORY_NAME_MAX_LENGTH = 40;

/** Shared by the rename UI and its test. Empty / unchanged names are no-ops. */
export const validateCategoryName = (
  value: string,
): { isValid: boolean; error?: string } => {
  const trimmed = value.trim();
  if (!trimmed) return { isValid: false, error: "Name is required" };
  if (trimmed.length > CATEGORY_NAME_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Name cannot exceed ${CATEGORY_NAME_MAX_LENGTH} characters`,
    };
  }
  return { isValid: true };
};

/** Options (with crown tiers derived) + the default selection. */
const CategoryOptionRow = ({
  option,
  isRenameEnabled,
}: {
  option: CustomerUserCategory;
  isRenameEnabled: boolean;
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(option.name);
  const [error, setError] = useState<string | undefined>(undefined);

  const { mutateAsync: renameCategory, isLoading: isSaving } =
    useUpdateCustomerUserCategory();

  const startEditing = () => {
    setDraft(option.name);
    setError(undefined);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setError(undefined);
  };

  const save = async () => {
    const result = validateCategoryName(draft);
    if (!result.isValid) {
      setError(result.error);
      return;
    }
    const name = draft.trim();
    // Nothing changed — close without spending a request.
    if (name === option.name) {
      cancelEditing();
      return;
    }
    try {
      await renameCategory({ mappingId: option.mapping_id, name });
      toast.success("Category renamed successfully.");
      setIsEditing(false);
    } catch (e) {
      setError(getApiErrorMessage(e, "Unable to rename category."));
    }
  };

  if (isEditing) {
    return (
      <Stack
        direction="row"
        gap="4px"
        alignItems="flex-start"
        sx={{ width: "100%", minWidth: 0 }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Input
            name={`category_name_${option.mapping_id}`}
            autoFocus
            isResponsive={false}
            size="small"
            hideHelperBox
            value={draft}
            disabled={isSaving}
            error={!!error}
            errorMessage={error}
            inputProps={{ maxLength: CATEGORY_NAME_MAX_LENGTH }}
            onChange={(
              event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              setDraft(event.target.value);
              if (error) setError(undefined);
            }}
            onKeyDown={(event: KeyboardEvent) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void save();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                cancelEditing();
              }
            }}
          />
        </Box>
        <IconButton
          aria-label={`Save name for ${option.name}`}
          size="small"
          disabled={isSaving}
          onClick={() => void save()}
        >
          <CheckIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label={`Cancel renaming ${option.name}`}
          size="small"
          disabled={isSaving}
          onClick={cancelEditing}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      gap="8px"
      alignItems="center"
      sx={{
        width: "100%",
        minWidth: 0,
        // CSS hover, not JS state: a pointer crossing an open menu would
        // otherwise re-render a row per mouseover.
        "& .category-rename-action": { opacity: 0, pointerEvents: "none" },
        "&:hover .category-rename-action": {
          opacity: 1,
          pointerEvents: "auto",
        },
      }}
    >
      <CrownTierIcon tier={option.tier ?? null} />
      <Typography
        variant="body-medium"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {option.name}
      </Typography>
      {isRenameEnabled && (
        // Always mounted so the hover reveal is a paint, not a mount — a
        // freshly mounted button under the cursor never receives the click that
        // revealed it.
        <IconButton
          className="category-rename-action"
          aria-label={`Rename ${option.name}`}
          size="small"
          sx={{ color: theme.palette.lightBg.medium }}
          onClick={(event) => {
            event.stopPropagation();
            startEditing();
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
};

export const CategorySelectField = ({
  control,
  name = "category",
  label = "Category",
  /** Rename is offered only where the design asks for it. */
  isRenameEnabled = true,
  disabled = false,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: RHF Control is generic across form shapes
  control: any;
  name?: string;
  label?: string;
  isRenameEnabled?: boolean;
  /** Read-only surfaces (e.g. a user without full customer access) pass true. */
  disabled?: boolean;
}) => {
  const { options } = useCustomerUserCategoryOptions();

  return (
    <SelectField
      control={control}
      name={name}
      label={label}
      disabled={disabled}
      placeholder="Select category"
      options={options}
      valueMappingKey="mapping_id"
      labelMappingKey="name"
      keepMenuItemOriginalCasing
      renderValue={(_value, option) =>
        option ? (
          <Stack direction="row" gap="8px" alignItems="center">
            <CrownTierIcon tier={option?.tier ?? null} />
            <Typography variant="body-medium">{option?.name}</Typography>
          </Stack>
        ) : null
      }
      // `menuItemBuilder` renders INSIDE SelectField's own <MenuItem>, so this
      // must return the row's CONTENT only. Returning a <MenuItem> here nested
      // one <li> inside another and gave every row a second set of paddings.
      menuItemBuilder={({ item }) => (
        <CategoryOptionRow
          option={item as CustomerUserCategory}
          isRenameEnabled={isRenameEnabled}
        />
      )}
    />
  );
};
